import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { OrderIncompleteReason } from "models/OrderIncompleteReason";
import { toast } from "react-toastify";
import { useCreateReasonMutation, useUpdateReasonMutation } from "app/api/order-incomplete-reasons";
import { getErrorMessage } from "utilities/getErrorMessage";
import LargeDialog from "components/LargeDialog";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  TextField,
} from "@mui/material";
import Clear from "@mui/icons-material/Clear";
import Check from "@mui/icons-material/Check";
import { UpdateReasonRequest } from "api/order-incomplete-reasons/updateReason";

type UpsertReasonDialogProps = {
  reason?: OrderIncompleteReason | null;
  onClose: () => void;
};

const UpsertReasonDialog: React.FC<UpsertReasonDialogProps> = (props) => {
  const { reason: reasonToEdit, onClose } = props;

  const [createReason, { isLoading: isCreating }] = useCreateReasonMutation();
  const [updateReason, { isLoading: isUpdating }] = useUpdateReasonMutation();

  const isNew = !reasonToEdit;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<UpdateReasonRequest>();

  const onSubmit: SubmitHandler<UpdateReasonRequest> = (data) => {
    (isNew ? createReason(data) : updateReason({ ...reasonToEdit, ...data }))
      .unwrap()
      .then(() => {
        toast.success(`Anledningen ${isNew ? "skapades" : "uppdaterades"}`);
        onClose();
      })
      .catch((e) => {
        if (e.status === 409)
          setError("id", { type: "manual", message: "Anledning med detta ID finns redan" });
        else toast.error(getErrorMessage(e));
      });
  };

  return (
    <LargeDialog
      title={isNew ? "Skapa anledning" : "Redigera anledning"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isCreating || isUpdating}
            startIcon={<Clear />}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            loading={isCreating || isUpdating}
            color="success"
            startIcon={<Check />}
          >
            {isNew ? "Skapa" : "Spara"}
          </Button>
        </>
      }
    >
      <Controller
        name="id"
        control={control}
        defaultValue={reasonToEdit?.id}
        rules={{
          required: "ID krävs",
          pattern: {
            value: /^[A-Z0-9_]{1,16}$/,
            message:
              "ID får endast innehålla versaler, siffror och understreck. Ej Å Ä Ö. Max 16 tecken.",
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label="ID"
            type="text"
            autoComplete="new-password"
            variant="filled"
            disabled={!isNew}
            error={!!errors.id}
            helperText={errors.id?.message}
            slotProps={{ htmlInput: { maxlength: 16 } }}
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        defaultValue={reasonToEdit?.description}
        rules={{ required: "Beskrivning krävs" }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Beskrivning"
            type="text"
            autoComplete="new-password"
            variant="filled"
            error={!!errors.description}
            helperText={errors.description?.message}
            slotProps={{ htmlInput: { maxlength: 255 } }}
          />
        )}
      />

      <Controller
        name="archived"
        control={control}
        defaultValue={reasonToEdit?.archived}
        render={({ field }) => (
          <FormControl hidden={!!isNew}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Arkiverad"
              />
            </FormGroup>
            <FormHelperText sx={{ m: 0 }}>Arkiverade anledningar visas inte i appen</FormHelperText>
          </FormControl>
        )}
      />
    </LargeDialog>
  );
};

export default UpsertReasonDialog;
